import { useState, useEffect, useCallback } from 'react';
import { getSupabaseClient } from '@/lib/supabase';
import {
  SITE_SETTING_KEYS,
  SiteSettingsState,
  IdUrlSetting,
} from '@/lib/siteSettings';

const EMPTY_STATE: SiteSettingsState = {
  homeHeroImage: null,
  homeHeroImages: {},
  homeCtaImage: null,
  homeAiPreviewImages: {},
  homeTestimonialAvatars: {},
  productFinishingThumbnails: {},
  homeCategoryImages: {},
};

function parseIdUrlMap(value: unknown): Record<string, string> {
  const map: Record<string, string> = {};
  if (!Array.isArray(value)) return map;
  value.forEach((item) => {
    if (
      item &&
      typeof item === 'object' &&
      typeof (item as IdUrlSetting).id === 'string' &&
      typeof (item as IdUrlSetting).url === 'string'
    ) {
      map[(item as IdUrlSetting).id] = (item as IdUrlSetting).url;
    }
  });
  return map;
}

export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettingsState>(EMPTY_STATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('site_settings')
        .select('key, value');

      if (dbError) throw dbError;

      const next: SiteSettingsState = {
        homeHeroImage: null,
        homeHeroImages: {},
        homeCtaImage: null,
        homeAiPreviewImages: {},
        homeTestimonialAvatars: {},
        productFinishingThumbnails: {},
        homeCategoryImages: {},
      };

      (data || []).forEach((row: { key: string; value: unknown }) => {
        switch (row.key) {
          case SITE_SETTING_KEYS.homeHeroImage:
            next.homeHeroImage = typeof row.value === 'string' ? row.value : null;
            break;
          case SITE_SETTING_KEYS.homeHeroImages:
            next.homeHeroImages = parseIdUrlMap(row.value);
            break;
          case SITE_SETTING_KEYS.homeCtaImage:
            next.homeCtaImage = typeof row.value === 'string' ? row.value : null;
            break;
          case SITE_SETTING_KEYS.homeAiPreviewImages:
            next.homeAiPreviewImages = parseIdUrlMap(row.value);
            break;
          case SITE_SETTING_KEYS.homeTestimonialAvatars:
            next.homeTestimonialAvatars = parseIdUrlMap(row.value);
            break;
          case SITE_SETTING_KEYS.productFinishingThumbnails:
            next.productFinishingThumbnails = parseIdUrlMap(row.value);
            break;
          case SITE_SETTING_KEYS.homeCategoryImages:
            next.homeCategoryImages = parseIdUrlMap(row.value);
            break;
          default:
            break;
        }
      });

      setSettings(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load site settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setSetting = useCallback(async (key: string, value: unknown) => {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    setSaving(true);
    try {
      const { error: dbError } = await supabase
        .from('site_settings')
        .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
      if (dbError) throw dbError;

      setSettings((prev) => {
        const next: SiteSettingsState = {
          ...prev,
          homeHeroImages: { ...prev.homeHeroImages },
          homeAiPreviewImages: { ...prev.homeAiPreviewImages },
          homeTestimonialAvatars: { ...prev.homeTestimonialAvatars },
          productFinishingThumbnails: { ...prev.productFinishingThumbnails },
          homeCategoryImages: { ...prev.homeCategoryImages },
        };
        switch (key) {
          case SITE_SETTING_KEYS.homeHeroImage:
            next.homeHeroImage = typeof value === 'string' ? value : null;
            break;
          case SITE_SETTING_KEYS.homeHeroImages:
            next.homeHeroImages = parseIdUrlMap(value);
            break;
          case SITE_SETTING_KEYS.homeCtaImage:
            next.homeCtaImage = typeof value === 'string' ? value : null;
            break;
          case SITE_SETTING_KEYS.homeAiPreviewImages:
            next.homeAiPreviewImages = parseIdUrlMap(value);
            break;
          case SITE_SETTING_KEYS.homeTestimonialAvatars:
            next.homeTestimonialAvatars = parseIdUrlMap(value);
            break;
          case SITE_SETTING_KEYS.productFinishingThumbnails:
            next.productFinishingThumbnails = parseIdUrlMap(value);
            break;
          case SITE_SETTING_KEYS.homeCategoryImages:
            next.homeCategoryImages = parseIdUrlMap(value);
            break;
          default:
            break;
        }
        return next;
      });
    } finally {
      setSaving(false);
    }
  }, []);

  const removeSetting = useCallback(async (key: string) => {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    setSaving(true);
    try {
      const { error: dbError } = await supabase.from('site_settings').delete().eq('key', key);
      if (dbError) throw dbError;

      setSettings((prev) => {
        const next: SiteSettingsState = {
          ...prev,
          homeHeroImages: { ...prev.homeHeroImages },
          homeAiPreviewImages: { ...prev.homeAiPreviewImages },
          homeTestimonialAvatars: { ...prev.homeTestimonialAvatars },
          productFinishingThumbnails: { ...prev.productFinishingThumbnails },
          homeCategoryImages: { ...prev.homeCategoryImages },
        };
        switch (key) {
          case SITE_SETTING_KEYS.homeHeroImage:
            next.homeHeroImage = null;
            break;
          case SITE_SETTING_KEYS.homeHeroImages:
            next.homeHeroImages = {};
            break;
          case SITE_SETTING_KEYS.homeCtaImage:
            next.homeCtaImage = null;
            break;
          case SITE_SETTING_KEYS.homeAiPreviewImages:
            next.homeAiPreviewImages = {};
            break;
          case SITE_SETTING_KEYS.homeTestimonialAvatars:
            next.homeTestimonialAvatars = {};
            break;
          case SITE_SETTING_KEYS.productFinishingThumbnails:
            next.productFinishingThumbnails = {};
            break;
          case SITE_SETTING_KEYS.homeCategoryImages:
            next.homeCategoryImages = {};
            break;
          default:
            break;
        }
        return next;
      });
    } finally {
      setSaving(false);
    }
  }, []);

  const uploadImage = useCallback(async (file: File): Promise<string> => {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const ext =
      (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const safeBase =
      file.name
        .replace(/\.[^.]+$/, '')
        .replace(/[^a-z0-9_-]/gi, '-')
        .toLowerCase()
        .slice(0, 40) || 'image';
    const path = `site-assets/${Date.now()}-${safeBase}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('public')
      .upload(path, file, { cacheControl: '3600', upsert: false });
    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage.from('public').getPublicUrl(path);
    if (!urlData?.publicUrl) throw new Error('Failed to resolve uploaded image URL');
    return urlData.publicUrl;
  }, []);

  return {
    settings,
    loading,
    error,
    saving,
    setSetting,
    removeSetting,
    uploadImage,
    reload: load,
  };
}